import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Music, CheckCircle } from 'lucide-react';
import SpotifyTrackSelector from '@/components/sequences/SpotifyTrackSelector';
import { SpotifyTrack } from '@/services/spotifyService';
import { SequenceFormData } from '../types';

interface SpotifyIntegrationStepProps {
  form: UseFormReturn<SequenceFormData>;
  selectedTrack: SpotifyTrack | null;
  onTrackSelect: (track: SpotifyTrack) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

const SpotifyIntegrationStep: React.FC<SpotifyIntegrationStepProps> = ({
  form,
  selectedTrack,
  onTrackSelect,
  onNext,
  onBack,
  onSkip,
}) => {
  const handleTrackSelect = (track: SpotifyTrack) => {
    onTrackSelect(track);
    
    // Auto-populate form fields from Spotify data
    form.setValue('title', track.name);
    form.setValue('artist', track.artists[0]?.name || '');
    
    // Convert duration from milliseconds to seconds
    if (track.duration_ms) {
      form.setValue('duration', Math.round(track.duration_ms / 1000));
    }
    
    // Set album info if available
    if (track.album) {
      form.setValue('album', track.album.name);
      if (track.album.images?.[0]?.url) {
        form.setValue('album_cover_url', track.album.images[0].url);
      }
    }

    // Auto-detect genre based on track/artist/album names
    const detectedGenre = detectGenre(track);
    if (detectedGenre) {
      form.setValue('genre', detectedGenre);
    }
  };

  const detectGenre = (track: SpotifyTrack): string => {
    const title = track.name.toLowerCase();
    const artist = track.artists[0]?.name.toLowerCase() || '';
    const album = track.album.name.toLowerCase();
    
    // Common genre detection based on keywords
    if (title.includes('christmas') || title.includes('xmas') || title.includes('holiday') || 
        album.includes('christmas') || album.includes('holiday')) {
      return 'Christmas';
    }
    
    if (title.includes('rock') || artist.includes('rock') || album.includes('rock')) {
      return 'Rock';
    }
    
    if (title.includes('pop') || artist.includes('pop') || album.includes('pop')) {
      return 'Pop';
    }
    
    if (title.includes('country') || artist.includes('country') || album.includes('country')) {
      return 'Country';
    }
    
    if (title.includes('jazz') || artist.includes('jazz') || album.includes('jazz')) {
      return 'Jazz';
    }
    
    if (title.includes('classical') || artist.includes('classical') || album.includes('classical')) {
      return 'Classical';
    }
    
    return 'Contemporary';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Music className="h-5 w-5" />
          Link to Spotify (Optional)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-sm text-gray-600">
          <p className="mb-2">
            Linking to Spotify provides better metadata and helps users discover your sequence.
          </p>
          <p>
            This step is optional but recommended for better visibility.
          </p>
        </div>

        {!selectedTrack ? (
          <div className="space-y-4">
            <SpotifyTrackSelector
              onSelect={handleTrackSelect}
              placeholder="Search for the song on Spotify..."
            />
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Selecting the correct Spotify track will automatically 
                fill in the song details and provide album artwork for your sequence.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">Spotify track linked!</p>
                  <div className="mt-2 space-y-1 text-sm text-green-800">
                    <p><strong>Track:</strong> {selectedTrack.name}</p>
                    <p><strong>Artist:</strong> {selectedTrack.artists.map(a => a.name).join(', ')}</p>
                    <p><strong>Album:</strong> {selectedTrack.album.name}</p>
                    {selectedTrack.duration_ms && (
                      <p><strong>Duration:</strong> {Math.floor(selectedTrack.duration_ms / 60000)}:{String(Math.floor((selectedTrack.duration_ms % 60000) / 1000)).padStart(2, '0')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onTrackSelect(null!)}
              >
                Change Track
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
          <div className="flex gap-2">
            {!selectedTrack && (
              <Button type="button" variant="ghost" onClick={onSkip}>
                Skip This Step
              </Button>
            )}
            <Button 
              type="button" 
              onClick={onNext}
              disabled={false} // Allow proceeding even without Spotify
            >
              Next: Set Pricing
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SpotifyIntegrationStep;